// Libs
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid2 } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
// Components, Layouts, Pages
import { BaseSelect, Loading } from '~/components/common';
// Others
import { defaultValuesForm, schema } from './helper';
import { IFormChangeStatusJob, IJob } from '~/utils/interface/job';
import { IBaseOption } from '~/utils/interface/common';
import { mockDataStatusJob } from '~/utils/mockData/job';
import { useAppDispatch } from '~/utils/redux/hooks';
import { changeStatusJob } from '~/thunks/job/jobThunk';
import { jobActions } from '~/thunks/job/jobSlice';
import { PageTypeEnum } from '~/utils/enum/common';
// Styles, images, icons

type Props = {
  data?: IJob;
  type?: PageTypeEnum;
  onClose: () => void;
};

const ChangeStatusModal = (props: Props) => {
  //#region Destructuring Props
  const { data, type, onClose } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/job']);
  const dispatch = useAppDispatch();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<IFormChangeStatusJob>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesForm,
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!data?.status) return;

    reset({ status: data?.status });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.status]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleCloseChangeStatusModal = () => {
    onClose && onClose();
    reset(defaultValuesForm);
  };

  const handleSubmitChangeStatus = (dataForm: IFormChangeStatusJob) => {
    if (!dataForm || !data?.id) return;

    setIsLoading(true);

    dispatch(
      changeStatusJob({
        jobId: data?.id,
        body: dataForm,
      })
    )
      .unwrap()
      .then((res) => {
        if (type === PageTypeEnum.CUSTOMER) {
          dispatch(jobActions.setRefreshJobByCustomerList(true));
          return;
        }

        dispatch(jobActions.setRefreshJobList(true));
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
        handleCloseChangeStatusModal();
      });
  };
  //#endregion Handle Function

  return (
    <Dialog
      id='changeStatusModalComponent'
      open={true}
      onClose={handleCloseChangeStatusModal}
      fullWidth
      maxWidth={'xs'}
    >
      <Box component={'form'} onSubmit={handleSubmit(handleSubmitChangeStatus)}>
        <DialogTitle>{'Change Status'}</DialogTitle>

        <DialogContent dividers={true}>
          <Grid2 container rowSpacing={'24px'} columnSpacing={'24px'}>
            <Grid2 size={12}>
              <Controller
                name='status'
                control={control}
                render={({ field: { value, onChange } }) => (
                  <BaseSelect
                    label={t('form_change_status_label_status')}
                    value={value}
                    options={mockDataStatusJob || []}
                    onChange={({ value: optionValue }: IBaseOption) => onChange(optionValue)}
                    errorMessage={errors.status?.message}
                    required
                  />
                )}
              />
            </Grid2>
          </Grid2>

          {isLoading && <Loading />}
        </DialogContent>

        <DialogActions>
          <Button variant='outlined' onClick={handleCloseChangeStatusModal}>
            {t('common_action_cancel', { ns: 'common/base' })}
          </Button>

          <Button variant='contained' type='submit' disabled={!isDirty}>
            {t('common_action_save', { ns: 'common/base' })}
          </Button>
        </DialogActions>
      </Box>

      {isLoading && <Loading />}
    </Dialog>
  );
};

export default ChangeStatusModal;
